import { useState } from 'react';
import { GoldBadge } from '@/components/Luxury';
import { formatCurrency, formatPercent, formatCurrencyCompact } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';
import type { ExitScenarioResult, OfferSolution } from '../../types/calculations';

interface OfferPriceCardsProps {
  scenarios: ExitScenarioResult[];
  offerSolutions?: OfferSolution[];
  zandiOfferSolutions?: OfferSolution[];
  bestScenarioLabel: string;
  noi: number;
  equityRequired: number;
  capexBudget: number;
  selectedScenario?: string;
  onScenarioSelect?: (id: string) => void;
}

/** Expanded scenario labels matching wireframe spec */
const SCENARIO_LABELS: Record<string, string> = {
  'std-clean': 'Standard Depreciation \u2022 Clean Exit',
  'obbb-clean': 'OBBB Bonus Depr \u2022 Clean Exit',
  'std-1031': 'Standard Depreciation \u2022 1031 Exchange',
  'obbb-1031': 'OBBB Bonus Depr \u2022 1031 Exchange',
};

function scenarioKey(s: ExitScenarioResult): string {
  const bonus = s.bonus_depreciation ? 'obbb' : 'std';
  const exit = s.exit_strategy === '1031' ? '1031' : 'clean';
  return `${bonus}-${exit}`;
}

/** Get the corresponding standard scenario key for an OBBB scenario */
function getStandardCounterpart(key: string): string | null {
  if (key === 'obbb-clean') return 'std-clean';
  if (key === 'obbb-1031') return 'std-1031';
  return null;
}

/** Row helper for a label + value pair */
function DetailRow({ label, value, valueColor = 'text-ivory' }: {
  label: React.ReactNode; value: string; valueColor?: string;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-stone-700">{label}</span>
      <span className={`font-mono tabular-nums ${valueColor}`}>{value}</span>
    </div>
  );
}

export function OfferPriceCards({
  scenarios,
  offerSolutions,
  zandiOfferSolutions,
  bestScenarioLabel,
  noi,
  capexBudget,
  selectedScenario,
  onScenarioSelect,
}: OfferPriceCardsProps) {
  const hasZandi = zandiOfferSolutions && zandiOfferSolutions.length > 0;
  const [showZandi, setShowZandi] = useState(false);
  const activeSolutions = showZandi ? zandiOfferSolutions : offerSolutions;

  // Get Zandi rent growth for display
  const zandiRg = zandiOfferSolutions?.[0]?.rent_growth_used;
  const ssotRg = offerSolutions?.[0]?.rent_growth_used;

  return (
    <div>
      {/* Toggle header when Zandi data available */}
      {hasZandi && (
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm uppercase tracking-wider text-stone-700">
            Max Offer @ 15% IRR
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowZandi(false)}
              className={`text-sm px-2 py-0.5 rounded transition-colors ${
                !showZandi
                  ? 'bg-ivory/10 text-ivory'
                  : 'text-stone-700 hover:text-stone-700'
              }`}
            >
              SSOT {ssotRg != null ? `(${(ssotRg * 100).toFixed(1)}%)` : ''}
            </button>
            <button
              onClick={() => setShowZandi(true)}
              className={`text-sm px-2 py-0.5 rounded transition-colors ${
                showZandi
                  ? 'bg-gold/15 text-gold'
                  : 'text-stone-700 hover:text-stone-700'
              }`}
            >
              Zandi {zandiRg != null ? `(${(zandiRg * 100).toFixed(1)}%)` : ''}
            </button>
          </div>
        </div>
      )}

      {/* 2x2 detail grid */}
      <div className="grid grid-cols-2 gap-4">
        {scenarios.map((scenario) => {
          const key = scenarioKey(scenario);
          const offer = activeSolutions?.find(o => o.scenario === key);
          const ssotOffer = offerSolutions?.find(o => o.scenario === key);
          const zandiOffer = zandiOfferSolutions?.find(o => o.scenario === key);
          const isBest = scenario.label === bestScenarioLabel;

          // Use per-offer cap rate from backend, fallback to NOI-based calc
          const capRate = offer?.cap_rate_at_offer
            ?? (offer && offer.offer_price > 0 ? noi / offer.offer_price : undefined);

          // Zandi vs SSOT delta (when toggled)
          const zandiDelta = showZandi && ssotOffer && zandiOffer
            ? zandiOffer.offer_price - ssotOffer.offer_price
            : null;

          // OBBB purchasing power delta vs standard (same exit strategy)
          const stdCounterpart = getStandardCounterpart(key);
          const stdOffer = stdCounterpart
            ? activeSolutions?.find(o => o.scenario === stdCounterpart)
            : null;
          const obbDelta = stdOffer && offer
            ? offer.offer_price - stdOffer.offer_price
            : null;

          const isSelected = key === selectedScenario;

          return (
            <div
              key={key}
              onClick={() => onScenarioSelect?.(key)}
              className={`relative rounded-lg p-4 border cursor-pointer transition-all ${
                isSelected
                  ? 'border-gold/60 ring-1 ring-gold/30 shadow-[0_0_8px_rgba(212,175,55,0.15)] bg-gold/5'
                  : isBest
                    ? 'border-gold bg-gold/5 hover:border-gold/40'
                    : 'border-slate-dark bg-charcoal/40 hover:border-gold/40'
              }`}
            >
              {isSelected && (
                <span className="absolute top-2 right-2 text-sm uppercase tracking-wider text-gold bg-gold/10 px-1.5 py-0.5 rounded">
                  Viewing
                </span>
              )}
              {isBest && !isSelected && (
                <GoldBadge variant="gold" className="absolute -top-2 right-2 !text-sm !px-2 !py-0.5">
                  BEST
                </GoldBadge>
              )}

              {/* ---- HEADER ---- */}
              <div className="text-sm uppercase tracking-wider text-stone-700 mb-2 leading-tight">
                {SCENARIO_LABELS[key] ?? scenario.label}
              </div>

              {/* Offer price (large) + cap rate */}
              <div className="flex items-baseline justify-between mb-1">
                <div className={`text-xl font-mono tabular-nums font-bold ${isBest ? 'text-gold' : 'text-ivory'}`}>
                  {offer ? formatCurrency(offer.offer_price) : '\u2014'}
                </div>
                {capRate != null && (
                  <div className="text-sm text-stone-700 font-mono tabular-nums">
                    Cap: {formatPercent(capRate, 2)}
                  </div>
                )}
              </div>

              {/* Zandi delta (when toggled) */}
              {zandiDelta != null && zandiDelta !== 0 && (
                <div className={`text-sm font-mono tabular-nums mb-1 ${zandiDelta > 0 ? 'text-emerald' : 'text-crimson'}`}>
                  {zandiDelta > 0 ? '+' : ''}{formatCurrency(zandiDelta)} vs SSOT
                </div>
              )}

              {/* ---- INCOME (STABILIZED) ---- */}
              <div className="border-t border-slate-dark pt-2 mt-2 space-y-1 text-sm">
                <div className="text-sm uppercase tracking-wider text-stone-700">
                  Income (Stabilized)
                </div>
                <DetailRow
                  label="Gross Rent"
                  value={offer?.gross_rent != null ? formatCurrency(offer.gross_rent) : '\u2014'}
                />
                <DetailRow
                  label={`OpEx${offer?.opex_rate != null ? ` (${(offer.opex_rate * 100).toFixed(1)}% of EGI)` : ''}`}
                  value={offer?.opex_amount != null ? `-${formatCurrency(offer.opex_amount)}` : '\u2014'}
                  valueColor="text-stone-700"
                />
                <DetailRow
                  label="NOI"
                  value={offer?.stabilized_noi != null ? formatCurrency(offer.stabilized_noi) : '\u2014'}
                  valueColor="text-ivory"
                />
              </div>

              {/* ---- CAPITAL & RETURNS ---- */}
              <div className="border-t border-slate-dark pt-2 mt-2 space-y-1 text-sm">
                <div className="text-sm uppercase tracking-wider text-stone-700">
                  Capital & Returns
                </div>
                <DetailRow
                  label="Total Equity"
                  value={offer?.equity_required != null ? formatCurrency(offer.equity_required) : '\u2014'}
                />
                <DetailRow
                  label={`Loan${offer?.ltv != null ? ` (${(offer.ltv * 100).toFixed(0)}% LTV)` : ''}`}
                  value={offer?.loan_amount != null ? formatCurrency(offer.loan_amount) : '\u2014'}
                />
                <DetailRow
                  label={<MetricLabel metric="dscr">DSCR</MetricLabel>}
                  value={offer?.dscr != null ? `${offer.dscr.toFixed(2)}x` : '\u2014'}
                />
                <DetailRow
                  label={<MetricLabel metric="after_tax_irr">10yr After-Tax IRR</MetricLabel>}
                  value={offer?.irr_10yr != null ? formatPercent(offer.irr_10yr, 1) : '\u2014'}
                  valueColor={isBest ? 'text-gold' : 'text-ivory'}
                />
                <DetailRow
                  label={<MetricLabel metric="equity_multiple">Equity Multiple</MetricLabel>}
                  value={offer?.equity_multiple_10yr != null
                    ? `${offer.equity_multiple_10yr.toFixed(2)}x`
                    : offer?.equity_multiple_5yr != null
                      ? `${offer.equity_multiple_5yr.toFixed(2)}x (5yr)`
                      : '\u2014'}
                />
              </div>

              {/* ---- CAPEX & CONSTRUCTION ---- */}
              {capexBudget > 0 && (
                <div className="border-t border-slate-dark pt-2 mt-2 space-y-1 text-sm">
                  <div className="text-sm uppercase tracking-wider text-stone-700">
                    CapEx & Construction
                  </div>
                  <DetailRow
                    label="CapEx Budget"
                    value={formatCurrency(capexBudget)}
                  />
                  {(offer?.construction_carry_total ?? 0) > 0 && (
                    <DetailRow
                      label="Construction Carry"
                      value={formatCurrency(offer!.construction_carry_total!)}
                    />
                  )}
                  <DetailRow
                    label="Total Capital"
                    value={offer
                      ? formatCurrency((offer.equity_required || 0) + (offer.loan_amount || 0))
                      : '\u2014'}
                  />
                </div>
              )}

              {/* ---- RETURN BREAKDOWN ---- */}
              {(() => {
                const ybY = offer?.year_by_year;
                if (ybY && ybY.length > 0) {
                  const cumulativeCashFlow = ybY.reduce((sum, yr) => sum + (yr.after_tax_cf || 0), 0);
                  const cumulativeTaxBenefits = ybY.reduce((sum, yr) => sum + (yr.tax_benefit || 0), 0);
                  const netExitProceeds = [...ybY].reverse().find(yr => yr.exit_after_tax_net_proceeds != null)?.exit_after_tax_net_proceeds ?? 0;
                  const totalReturn = cumulativeCashFlow + cumulativeTaxBenefits + netExitProceeds;
                  return (
                    <div className="border-t border-slate-dark pt-2 mt-2 space-y-1 text-sm">
                      <div className="text-sm uppercase tracking-wider text-stone-700">
                        Return Breakdown
                      </div>
                      <DetailRow
                        label="Cumul. Cash Flow"
                        value={formatCurrency(cumulativeCashFlow)}
                      />
                      <DetailRow
                        label="Cumul. Tax Benefits"
                        value={formatCurrency(cumulativeTaxBenefits)}
                      />
                      <DetailRow
                        label="Net Exit Proceeds"
                        value={formatCurrency(netExitProceeds)}
                      />
                      <div className="border-t border-dashed border-slate-dark my-1" />
                      <DetailRow
                        label={<span className="font-medium">Total Return</span>}
                        value={formatCurrency(totalReturn)}
                        valueColor={isBest ? 'text-gold font-medium' : 'text-ivory font-medium'}
                      />
                    </div>
                  );
                }
                // Graceful fallback: no year_by_year, show single cumulative return
                return (
                  <div className="border-t border-slate-dark pt-2 mt-2 space-y-1 text-sm">
                    <div className="text-sm uppercase tracking-wider text-stone-700">
                      Return Breakdown
                    </div>
                    <DetailRow
                      label={<MetricLabel metric="cumulative_return">Total Return</MetricLabel>}
                      value={offer?.cumulative_after_tax_return_10yr != null
                        ? formatCurrency(offer.cumulative_after_tax_return_10yr)
                        : '\u2014'}
                      valueColor={isBest ? 'text-gold font-medium' : 'text-ivory font-medium'}
                    />
                  </div>
                );
              })()}

              {/* ---- OBBB COMPARISON LINE ---- */}
              {obbDelta != null && obbDelta > 0 && (
                <div className="mt-2 pt-1.5 text-sm text-emerald font-mono tabular-nums">
                  OBBB adds {formatCurrencyCompact(obbDelta)} purchasing power vs standard
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
